import React, { useCallback, useContext, useMemo } from 'react';
import { ApplicationCategoriesProps, ApplicationsProps } from '@config/useApps';
import SessionContext from '@providers/Session/SessionContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Divider } from 'antd';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import { ApplicationsConstants } from '@utils/applicationsConstants';
import * as S from './styles';

interface SectionApplicationsProps {
  setOpenedLevel?: Function;
  type?: 'menu' | 'home';
}

const SectionApplications = ({
  setOpenedLevel,
  type = 'menu',
}: SectionApplicationsProps) => {
  const { t } = useTranslation('main');
  const navigate = useNavigate();
  const {
    isRoot,
    selectedApp,
    setSelectedApp,
    allowedPagesRouting,
    isNotChildOfRoot,
    isMotBusiness,
    theme,
  } = useContext(SessionContext);
  const { keycloak } = useContext(KeycloakContext);
  const customerType = keycloak.idTokenParsed?.customerType;
  const isCustomer = customerType === 'CUSTOMER';

  const getFirstCategory = useCallback(
    (categories: ApplicationCategoriesProps[], hasLoraWan?: boolean) => {
      let firstCategory = categories?.find(
        category => category?.pages?.length > 0,
      );
      let deviceType = 'mobile-network';
      if (hasLoraWan) {
        const nonLoraWanItem = categories.find(item =>
          item.pages.some(
            page => !page.isLoraWan && !page.isCommon && !page.isDetails,
          ),
        );

        const hasOnlyCommonPages = nonLoraWanItem?.pages?.every(
          page => page.isCommon,
        );

        if (nonLoraWanItem && !hasOnlyCommonPages) {
          firstCategory = nonLoraWanItem;
        } else {
          deviceType = 'lorawan';
        }
      }
      return {
        firstCategory: {
          ...firstCategory,
          pages: firstCategory?.pages?.filter(item => !item.isDetails),
        },
        deviceType,
      };
    },
    [],
  );

  const handleClickAppItem = useCallback(
    (
      appName: ApplicationsConstants,
      categories: ApplicationCategoriesProps[],
      hasLoraWan?: boolean,
    ) => {
      setOpenedLevel?.('pages');
      setSelectedApp(appName);
      const { firstCategory, deviceType } = getFirstCategory(
        categories,
        hasLoraWan,
      );
      const firstCategoryName = firstCategory?.categoryName;
      const firstPage = firstCategory?.pages?.[0];
      const rootCannotSee = firstPage?.rootCannotSee && isRoot;

      if (!rootCannotSee) {
        const firstPagePath = firstPage?.path;
        const pathToNavigate = `/${appName}${
          hasLoraWan ? `/${deviceType}` : ''
        }${
          firstCategoryName !== appName ? `/${firstCategoryName}` : ''
        }/${firstPagePath}`;
        navigate(pathToNavigate);
      }
    },
    [getFirstCategory, isRoot, navigate, setOpenedLevel, setSelectedApp],
  );

  const applicationsByType = useMemo(() => {
    const products: ApplicationsProps[] = [];
    const apps: ApplicationsProps[] = [];
    allowedPagesRouting?.forEach(item => {
      if (item.type === 'product') {
        if (isMotBusiness && isCustomer) {
          return;
        }
        products.push(item);
      } else {
        apps.push(item);
      }
    });
    return {
      products,
      apps,
    };
  }, [allowedPagesRouting, isCustomer, isMotBusiness]);

  const handleRenderApp = useCallback(
    ({
      name,
      icon,
      alternativeName,
      categories,
      hasLorawan,
    }: ApplicationsProps) => {
      const title = t(
        `apps.${isNotChildOfRoot && alternativeName ? alternativeName : name}`,
      );

      return (
        categories.filter(item => item.pages.length > 0).length > 0 && (
          <S.AppItem
            key={title}
            className={`app-item ${icon ? 'app-item-with-icon' : ''} ${
              type === 'menu' ? 'menu-app-item' : 'home-app-item'
            } ${selectedApp === name ? 'selected-app' : ''}`}
            onClick={() => {
              handleClickAppItem(name, categories, hasLorawan);
            }}
            isDarkTheme={theme?.title === 'dark'}
          >
            <span className="app-item-icon">{icon}</span>
            <h1>{title}</h1>
          </S.AppItem>
        )
      );
    },
    [handleClickAppItem, isNotChildOfRoot, selectedApp, t, theme?.title, type],
  );

  const appsContent = useMemo(() => {
    const hasProducts = applicationsByType?.products?.length > 0;

    const allProducts = applicationsByType.products.map(item =>
      handleRenderApp(item),
    );
    const allApps = applicationsByType?.apps?.map(item =>
      handleRenderApp(item),
    );

    const dividerEl = (
      name: string,
      orientation: 'left' | 'center',
      className?: string,
    ) => (
      <Divider orientation={orientation} className={className}>
        {t(`apps.${name}`)}
      </Divider>
    );

    return {
      menu: (
        <S.AppsMenuContent>
          {hasProducts && (
            <>
              {dividerEl('your-apps', 'center')}
              {allProducts}
            </>
          )}

          {dividerEl('operations', 'center', 'apps-section-operations')}
          {allApps}
        </S.AppsMenuContent>
      ),
      home: (
        <S.AppsHomeContent>
          {hasProducts && (
            <>
              {dividerEl('your-apps', 'left')}
              {allProducts}
            </>
          )}
          {dividerEl(hasProducts ? 'operations' : 'your-apps', 'left')}
          {allApps}
        </S.AppsHomeContent>
      ),
    };
  }, [applicationsByType, handleRenderApp, t]);

  return <S.AppsContainer>{appsContent?.[type]}</S.AppsContainer>;
};

export default SectionApplications;
